/* Copyright (c) 2016-2025 Planticle Pty Ltd */
"use strict";

import birdata from "../api.js";
import ko from "knockout";
import fieldManager from "../util/fields.js";
import util from "../util/util.js";

const raptorNestSpecies = {
	id: -1,
	common_name: 'Raptor Nest'
};

var program = {
	code: "rgh",
	name: "Red Goshawk",
	description: `
		<p>The Red Goshawk (<em>Erythrotriorchis radiatus</em>), Australia’s rarest raptor, has declined across two-thirds of its historical breeding range. It is also one of the country’s least detected birds in surveys, highlighting significant knowledge gaps in its distribution and population trends.</p>
		<p>This project, <em>Developing a National Red Goshawk Monitoring Program</em>, aims to address these gaps by implementing targeted surveys and collection of breeding data to refine our understanding of the species' range and trends.</p>
		<p>With a long-term conservation focus, the project also supports Indigenous Ranger Groups in survey training and nest monitoring. The data collected will establish a crucial baseline to track population changes and assess future conservation efforts.</p>
		<p>Your participation contributes to vital research that will help protect this species for generations to come.</p>
	`,
	canFilterBy: function(user) {
		return user.is_administrator || user.permissions?.some(function(p) {
			return p.type == "program" && p.program == "rgh";
		});
	},
	load: function() {
		return Promise.all([birdata.lookup(), birdata.speciesList()]).then(([lookups, speciesList]) => {
			program.options = lookups;
			program.speciesList = speciesList.filter(sp => sp.supertaxa.includes("guild:Raptor"));
			return true;
		});
	},
	fields: function(survey) {
		var fields = [
			{
				name: "dataPolicyAcknowledged",
				type: "boolean",
				label: "I understand that data submitted under this program will not made publicly available, but will be used by BirdLife for research/conservation.",
				validate: function(f) {
					if(f.value() !== true) {
						return "Data policy must be acknowledged"
					}
				},
				defaultValue: localStorage.rghDataPolicyAcknowlegded === "true"
			},
			{
				name: "observerNames",
				type: "text",
				label: "Observer names",
				validate: "required"
			},
			{
				name: "groupName",
				type: "text",
				label: "Group/organisation",
				validate: "required"
			},
			{
				name: "rghSurveyType",
				type: "options",
				options: program.options.rgh_survey_type,
				label: "Survey type",
				validate: "required"
			},
			{
				name: "temperatureCelsius",
				type: "number",
				label: "Approximate temperature",
				units: "°C",
				max: 80,
				min: -30,
				decimalPlaces: 1
			},
			{
				name: "windSpeed",
				label: "Average wind speed",
				type: "options",
				// style: "radio",
				options: program.options.bof_wind_speed
			},
			{
				name: "cloudCover",
				label: "Cloud",
				type: "options",
				// style: "radio",
				options: program.options.rgh_cloud_cover,
				validate: "required"
			}
		].map(fieldManager.field);

		fields = util.arrayToMap(fields, function(f) { return f.field.name; });

		fields.dataPolicyAcknowledged.subscribe(function(x) {
			localStorage.rghDataPolicyAcknowlegded = "" + x;
		});

		return fields;
	},
	sightingFields: function(sightingModel) {
		let fields;

		function isNest() {
			return fields.sightingType() == 'nest';
		}

		function isNestSpeciesKnown() {
			return isNest() && fields.speciesKnown()?.code == 'yes';
		}

		fields = [
			{
				name: "sightingType",
				type: "text",
				defaultValue: "general"
			},
			// General fields
			{
				name: "age",
				label: "Age",
				type: "options",
				options: program.options.rgh_age,
				multiselect: true,
				hidden: () => isNest()
			},
			{
				name: "sex",
				label: "Sex",
				type: "options",
				options: program.options.rgh_sex,
				hidden: () => isNest()
			},
			{
				name: "activity",
				label: "Main activity",
				type: "options",
				options: program.options.rgh_activity,
				hidden: () => isNest()
			},
			{
				name: "otherActivity",
				label: "Other activity",
				type: "text",
				validate: "required",
				placeholder: "Please describe",
				hidden: () => isNest() || fields.activity()?.code !== 'other'
			},
			// Nest fields
			{
				name: "speciesKnown",
				label: "Raptor species known",
				type: "options",
				options: [
					{ code: "no", name: "No (Unknown)" },
					{ code: "maybe", name: "Maybe (no birds present)" },
					{ code: "yes", name: "Yes (birds present)" }
				],
				validate: "required",
				hidden: () => !isNest()
			},
			{
				name: "nestSpecies",
				label: "Raptor species",
				type: "options",
				optionsText: "common_name",
				lookupKey: "id",
				searchable: true,
				options: program.speciesList,
				hidden: () => !isNest() || (fields.speciesKnown()?.code ?? 'no') === 'no',
				validate: "required"
			},
			{
				name: "identificationReasoning",
				label: "Reasoning for identification",
				type: "text",
				multiline: true,
				validate: "required",
				hidden: () => !isNest() || fields.speciesKnown()?.code != 'maybe'
			},
			// Nest fields - with birds present
			{
				name: "ageSex",
				label: "Ages/sexes",
				type: "options",
				multiselect: true,
				//style: "checkbox",
				options: program.options.rgh_age_sex,
				validate: "required",
				hidden: () => !isNestSpeciesKnown()
			},
			{
				name: "juvenileCount",
				label: "# Juveniles",
				type: "integer",
				max: 99,
				defaultValue: 0,
				validate: "required",
				hidden: () => !isNestSpeciesKnown() ||
					!fields.ageSex()?.some(x => x.code.startsWith("juveniles_"))
			},
			{
				name: "breedingStatus",
				label: "Breeding status",
				type: "options",
				options: program.options.rgh_breeding_status,
				validate: "required",
				hidden: () => !isNestSpeciesKnown()
			},
			{
				name: "nestFailureComments",
				label: "Nest failure comments",
				type: "text",
				multiline: true,
				validate: "required",
				hidden: () => !isNestSpeciesKnown() || fields.breedingStatus()?.code !== 'nest_failure'
			},
			{
				name: "nestActivity",
				label: "Main activity",
				type: "options",
				multiselect: true,
				//style: "checkbox",
				options: program.options.rgh_nest_activity,
				validate: "required",
				hidden: () => !isNestSpeciesKnown()
			},
			{
				name: "nestlingAge",
				label: "Age of nestlings",
				type: "options",
				options: program.options.rgh_nestling_age,
				validate: "required",
				hidden: () => !isNestSpeciesKnown() || !fields.ageSex()?.some(x => x.code === "juveniles_nestlings")
			},
			// Nest fields - regardless of birds present
			{
				name: "treeSpecies",
				label: "Tree species (if known)",
				type: "text",
				hidden: () => !isNest()
			},
			{
				name: "nestType",
				label: "Nest type",
				type: "options",
				options: program.options.rgh_nest_type,
				validate: "required",
				hidden: () => !isNest()
			},
			{
				name: "nestName",
				label: "Nest ID / Nest name",
				type: "text",
				hidden: () => !isNest()
			},
			{
				name: "preyRemainsPresent",
				label: "Collected/observed prey remains",
				type: "boolean",
				defaultValue: false,
				validate: "required",
				hidden: () => !isNest()
			},
			{
				name: "preyRemains",
				label: "Prey remains details",
				type: "text",
				multiline: true,
				validate: "required",
				hidden: () => !isNest() || !fields.preyRemainsPresent()
			},
			{
				name: "locationAccuracyInMetres",
				label: "Location accuracy",
				type: "integer",
				units: "m"
			}
		].map(fieldManager.field);

		fields = util.arrayToMap(fields, function(f) { return f.field.name; });

		return fields;
	},
	programData: function(survey) {
		var fields = program.fields(survey);

		var speciesList = ko.observable(program.speciesList);

		return {
			fields: fields,
			validations: Object.values(fields),
			speciesList: speciesList
		};
	},
	multipleSightingsPerSpecies: true,
	allSpeciesRecordedEnabled: (survey) => false,
	visibleSharedSiteLayers: () => [],
	filterAreaLayers: (layers) => [],
	defaultAreaLayer: (layers) => null,
	multipleDaysEnabled: function() { return false; },
	updateSurveyJSON: function(json, survey) {
		var fields = survey.programData().rgh.fields;
		json.rgh = {};

		for(var f of Object.values(fields)) {
			f.field.save(json.rgh);
		}

		let sightings = json.sightings;

		sightings.forEach(function(sighting, index) {
			sighting.entry_order = index;
			let s = sighting;
			if(s.rgh.sighting_type == 'nest') {
				s.rgh.birds_present = s.rgh.species_known == 'yes';
				s.rgh.identification_reasoning = s.rgh.species_known == 'maybe' ? s.rgh.identification_reasoning : undefined;
				s.rgh.species_id = s.rgh.nest_species;
				delete s.rgh.nest_species;
				if(!s.rgh.birds_present) {
					// Flag that this is not a 'real' sighting and should not be processed as such
					s.is_pseudo = true;
					delete s.count;
					delete s.taxonomy_id;
					delete s.subspecies_ids;
					delete s.breeding_activity;
					delete s.id;
					delete s.species_id;
				} else {
					sighting.rgh.local_id = index + '';
					s.count = s.rgh.age_sex.filter(x => !x.startsWith("juveniles_")).length + (s.rgh.juvenile_count ?? 0);
					s.species_id = s.rgh.species_id;
				}

				if(!s.rgh.prey_remains_present) {
					delete s.rgh.prey_remains;
				}
				delete s.rgh.prey_remains_present;
			}
			delete s.rgh.species_known;
		});

		delete json.rgh.data_policy_acknowledged;
	},
	updateSightingJSON: function(json, sighting, survey) {
		json.rgh.nest_id = sighting.originalJSON?.rgh?.nest_id;
	},
	updateSightingsFromJSON(json, sightings, survey) {
	},
	hideWaterLevel: true,
	hideSurveyType: true,
	surveyTypeOptions: [],
	afterSelect: function(survey) {
		survey.type(program.options.survey_type.find(t => t.code == 'incidental_search'));
        survey.multipleDays(false);
		survey.allSpeciesRecorded(false);
	},
	beforeDeselect: function(survey) {
		survey.type(null);
	},
	preprocessJSON: function(json) {
		if(!json.rgh) return;

		json.rgh.data_policy_acknowledged = true;

		json.sightings?.forEach(s => {
			if(s.rgh.sighting_type == 'nest') {
				s.rgh.species_known = s.rgh.birds_present ? 'yes' : s.rgh.species_id ? 'maybe' : 'no';
				s.rgh.nest_species = s.rgh.species_id;
				s.species = raptorNestSpecies;
				s.rgh.prey_remains_present = !!s.rgh.prey_remains;
			}
		});
	},
	loadFromJSON: function(json, fields, survey) {
		var data = json.rgh;
		if(!data) return;

		program.preprocessJSON(json);

		for(var f of Object.values(fields)) {
			f.field.load(data);
		}
	},
	forcePrivate: true,
	sightingLocations: true,
	// forceSightingLocations: true
};

export default program;
